import { Card } from '@/components/ui/card';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminVenueDetailModal from './AdminVenueDetailModal';
import AdminAlertsDrawer from './AdminAlertsDrawer';
import {
  Users, Calendar, Heart, Ghost, Tag, Footprints,
  LayoutDashboard, BarChart3, Megaphone, CalendarCheck, Handshake,
  ChevronRight, Maximize2, ExternalLink, Bell,
  ShieldCheck, Gauge, Star, UsersRound, TrendingUp, PlayCircle
} from 'lucide-react';

/**
 * NightBuZz Admin OS Command Center — matches the master v1.0.0 mockup.
 *  - Left column:  PATRONS heading + tagline, vertical RED "PATRON ANALYTICS" pill,
 *                  6 sub-menu items, Tutorial Video card with checklist.
 *  - Center column: NIGHTBUZZ hero title, Colonel Medulla image with 4 live stat blocks,
 *                  Top Venues + Top Events lists, Age Groups donut, Peak Hours bars,
 *                  5 status pill tiles, Featured Partner ribbon.
 *  - Right column: BAR OWNERS heading + tagline, vertical BLUE "PARTNER ANALYTICS" pill,
 *                  6 sub-menu items, WHY NIGHTBUZZ? bullet list.
 *  - Footer: Connected Ecosystem logo strip.
 */

const GOLD = '#FFD700';
const RED = '#FF3B30';
const CYAN = '#00CCFF';
const GREEN = '#00FF41';

const HERO_IMG =
  'https://static.prod-images.emergentagent.com/jobs/8256134e-a862-4a68-83b5-73118a4b2925/images/d4cf2c1a02d69ae1f9a06ab39f7d9fae877d6be582025b8f17af7a7c30f18305.png';
const TUTORIAL_VIDEO = '/nightbuzz-tutorial.mp4';

const PATRON_MENU = [
  { label: 'Live Crowds',      Icon: Users,      path: '/home' },
  { label: 'Events Near You',  Icon: Calendar,   path: '/singles-night' },
  { label: 'Bar Crawls',       Icon: Footprints, path: '/bar-crawl' },
  { label: 'Singles / Mingle', Icon: Heart,      path: '/matchmaking' },
  { label: 'Ghost Mode',       Icon: Ghost,      path: '/profile?section=ghost-mode' },
  { label: 'Exclusive Deals',  Icon: Tag,        path: '/premium' },
];

const OWNER_MENU = [
  { label: 'Business Dashboard',    Icon: LayoutDashboard, path: '/bar-owner/dashboard' },
  { label: 'Analytics & Insights',  Icon: BarChart3,       path: '/bar-owner/dashboard?tab=analytics' },
  { label: 'Promotions & Deals',    Icon: Megaphone,       path: '/bar-owner/dashboard?tab=promote' },
  { label: 'Reservations',          Icon: CalendarCheck,   path: '/reservations' },
  { label: 'Customer Insights',     Icon: Users,           path: '/bar-owner/dashboard?tab=patrons' },
  { label: 'Partner Opportunities', Icon: Handshake,       path: '/advertiser/auth' },
];

const TOP_VENUES = [
  { rank: 1, name: 'Neon Nights' },
  { rank: 2, name: 'The Vault' },
  { rank: 3, name: 'Skyline Rooftop' },
  { rank: 4, name: 'Underground' },
  { rank: 5, name: 'Pulse Lounge' },
];

const TOP_EVENTS = [
  { rank: 1, name: 'Ladies Night' },
  { rank: 2, name: 'DJ Set 90s' },
  { rank: 3, name: 'Wine Hour' },
  { rank: 4, name: 'College Night' },
];

const AGE_GROUPS = [
  { g: '21-25', pct: 42, color: RED },
  { g: '26-30', pct: 30, color: CYAN },
  { g: '31-35', pct: 18, color: GOLD },
  { g: '36+',   pct: 10, color: GREEN },
];

const PEAK_HOURS = [
  { h: '8P', v: 30 }, { h: '9P', v: 55 }, { h: '10P', v: 78 },
  { h: '11P', v: 92 }, { h: '12A', v: 100 }, { h: '1A', v: 74 }, { h: '2A', v: 42 },
];

const STATUS_TILES = [
  { emoji: '🆔', label: 'AGE VERIFIED' },
  { emoji: '🛡️', label: 'SAFETY CHECK' },
  { emoji: '⚖️', label: 'RATIO CHECK' },
  { emoji: '⭐', label: 'RATINGS' },
  { emoji: '✅', label: 'VERIFIED' },
];

const WHY_LIST = [
  { Icon: Gauge,        title: 'Real-Time Data',    body: 'Always up to the minute.' },
  { Icon: TrendingUp,   title: 'Smarter Decisions', body: 'AI insights that matter.' },
  { Icon: UsersRound,   title: 'More Customers',    body: 'Increase foot traffic 3x.' },
  { Icon: Star,         title: 'Stronger Community',body: 'Connect with your regulars.' },
  { Icon: ShieldCheck,  title: 'Built for Nightlife', body: 'By people who live it.' },
];

const ECOSYSTEM = [
  { label: 'Google Maps', key: 'google_places', url: 'https://www.google.com/s2/favicons?domain=maps.google.com&sz=64' },
  { label: 'Stripe',      key: 'stripe',        url: 'https://www.google.com/s2/favicons?domain=stripe.com&sz=64' },
  { label: 'Spotify',     key: 'spotify',       url: 'https://www.google.com/s2/favicons?domain=spotify.com&sz=64' },
  { label: 'Instagram',   key: 'instagram',     url: 'https://www.google.com/s2/favicons?domain=instagram.com&sz=64' },
  { label: 'Uber',        key: 'uber',          url: 'https://www.google.com/s2/favicons?domain=uber.com&sz=64' },
  { label: 'Lyft',        key: 'lyft',          url: 'https://www.google.com/s2/favicons?domain=lyft.com&sz=64' },
  { label: 'Yelp',        key: 'yelp',          url: 'https://www.google.com/s2/favicons?domain=yelp.com&sz=64' },
  { label: 'Resend',      key: 'resend',        url: 'https://www.google.com/s2/favicons?domain=resend.com&sz=64' },
  { label: 'Twitter',     key: 'twitter',       url: 'https://www.google.com/s2/favicons?domain=twitter.com&sz=64' },
  { label: 'TikTok',      key: 'tiktok',        url: 'https://www.google.com/s2/favicons?domain=tiktok.com&sz=64' },
  { label: 'VAPID',       key: 'vapid',         url: 'https://www.google.com/s2/favicons?domain=web.dev&sz=64' },
  { label: 'Google Auth', key: 'google_auth',   url: 'https://www.google.com/s2/favicons?domain=google.com&sz=64' },
];

function ageDonutBackground() {
  let acc = 0;
  const stops = AGE_GROUPS.map((a) => {
    const from = acc; acc += a.pct;
    return `${a.color} ${from}% ${acc}%`;
  });
  return `conic-gradient(${stops.join(', ')})`;
}

const requestFs = (el) => {
  if (!el) return;
  const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
  if (req) req.call(el);
};
const openInWindow = (view) => {
  const url = `${window.location.pathname}?view=${view}`;
  window.open(url, `admin_${view}`, 'width=900,height=1200,noopener');
};
const ColumnControls = ({ view, targetRef }) => (
  <div className="absolute top-2 right-2 flex gap-1 z-10">
    <button data-testid={`col-fullscreen-${view}`} onClick={() => requestFs(targetRef.current)} title="Fullscreen"
      className="p-1 rounded bg-black/60 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/40">
      <Maximize2 className="w-3 h-3" />
    </button>
    <button data-testid={`col-popout-${view}`} onClick={() => openInWindow(view)} title="New window"
      className="p-1 rounded bg-black/60 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/40">
      <ExternalLink className="w-3 h-3" />
    </button>
  </div>
);

/** Vertical rounded pill — the big red/blue capsule with vertical text. */
const VerticalPill = ({ label, color, glow, testid, onClick }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="relative flex-shrink-0 flex items-center justify-center rounded-full transition hover:scale-105"
    style={{
      width: 78,
      minHeight: 260,
      background: `radial-gradient(ellipse at 30% 30%, ${color}dd, ${color}55 55%, #0a0a0a)`,
      border: `3px solid ${color}`,
      boxShadow: `0 0 40px ${glow}, inset 0 0 20px ${color}66`,
      fontFamily: 'Orbitron, sans-serif',
    }}
    aria-label={label}
  >
    <span
      className="font-black tracking-widest text-white uppercase whitespace-nowrap"
      style={{
        writingMode: 'vertical-rl',
        transform: 'rotate(180deg)',
        fontSize: '1rem',
        letterSpacing: '0.28em',
        textShadow: '0 0 8px rgba(255,255,255,0.6)',
      }}
    >
      {label}
    </span>
  </button>
);

const MenuRow = ({ Icon, label, accent, onClick, testid }) => {
  const palette = accent === 'sky'
    ? 'text-sky-200 border-sky-500/50 hover:bg-sky-900/25 hover:border-sky-300'
    : 'text-emerald-200 border-emerald-500/50 hover:bg-emerald-900/25 hover:border-emerald-300';
  const iconColor = accent === 'sky' ? 'text-sky-300' : 'text-emerald-300';
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`group w-full flex items-center justify-between gap-2 py-2 px-3 rounded-lg border ${palette} bg-black/60 transition font-mono text-[13px] tracking-wide`}
    >
      <span className="flex items-center gap-2 truncate">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="truncate">{label}</span>
      </span>
      <ChevronRight className={`w-4 h-4 ${iconColor} group-hover:translate-x-1 transition-transform`} />
    </button>
  );
};

/** Live stat pill sitting near Colonel Medulla, flanked by a drink glass. */
const HeroStat = ({ label, value, delta, color = GREEN, glass = '🍺', side = 'left' }) => (
  <div className="flex items-center gap-2">
    {side === 'left' && (
      <span className="text-3xl sm:text-4xl leading-none select-none" style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}>{glass}</span>
    )}
    <div
      className="rounded-lg px-3 py-2 flex-1"
      style={{
        background: 'linear-gradient(180deg, rgba(0,20,5,0.9), rgba(0,0,0,0.85))',
        border: `1px solid ${color}88`,
        boxShadow: `0 0 12px ${color}55 inset`,
      }}
    >
      <p className="text-[9px] tracking-widest font-mono" style={{ color, textShadow: `0 0 4px ${color}` }}>{label}</p>
      <p className="text-2xl sm:text-3xl font-black text-white leading-none" style={{ fontFamily: 'Orbitron, sans-serif' }}>{value}</p>
      {delta && <p className="text-[10px] font-mono" style={{ color }}>{delta}</p>}
    </div>
    {side === 'right' && (
      <span className="text-3xl sm:text-4xl leading-none select-none" style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}>{glass}</span>
    )}
  </div>
);

export default function AdminCommandCenter({ stats, aggregates, overview, systemHealth, adminKey }) {
  const navigate = useNavigate();
  const patronsRef = useRef(null);
  const medullaRef = useRef(null);
  const ownersRef = useRef(null);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const singleView = params?.get('view');
  const showPatrons = !singleView || singleView === 'patrons';
  const showMedulla = !singleView || singleView === 'medulla';
  const showOwners  = !singleView || singleView === 'owners';

  // Open internal routes in a new tab so admin keeps their command-center session
  const go = (path) => { if (path) window.open(path, '_blank', 'noopener'); };

  const usersOnline  = (stats?.total_patrons ?? overview?.total_users ?? 0).toLocaleString();
  const ownersOnline = (stats?.total_bar_owners ?? overview?.total_owners ?? 0).toLocaleString();
  const checkinsToday = (aggregates?.checkins_today?.total ?? overview?.live_checkins ?? 0).toLocaleString();
  const girls = stats?.female_count ?? 0;
  const guys  = stats?.male_count ?? 0;
  const total = girls + guys || 1;
  const girlsPct = Math.round((girls / total) * 100) || 52;
  const guysPct  = 100 - girlsPct;

  // Peak hours — real when aggregates available, otherwise fall back to demo curve
  const peakHours = (aggregates?.checkins_today?.peak_hours && aggregates.checkins_today.peak_hours.some(b => b.count > 0))
    ? aggregates.checkins_today.peak_hours.map(b => ({ h: b.h, v: b.pct }))
    : PEAK_HOURS;

  // Age groups — real when aggregates available and non-empty
  const ageGroups = (aggregates?.age_mix?.groups && aggregates.age_mix.total > 0)
    ? aggregates.age_mix.groups.map((a, i) => ({ g: a.g, pct: a.pct, color: [RED, CYAN, GOLD, GREEN][i] || GREEN }))
    : AGE_GROUPS;

  const ageDonutBg = (() => {
    let acc = 0;
    const stops = ageGroups.map((a) => {
      const from = acc; acc += a.pct;
      return `${a.color} ${from}% ${acc}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  })();

  const topVenues = (aggregates?.top_venues?.venues && aggregates.top_venues.venues.length)
    ? aggregates.top_venues.venues
    : TOP_VENUES;

  const topEvents = (aggregates?.top_events?.events && aggregates.top_events.events.length)
    ? aggregates.top_events.events
    : TOP_EVENTS;

  const isRealData = !!(aggregates?.checkins_today);

  return (
    <div data-testid="admin-command-center" className="space-y-4">
      {/* OS header bar */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-2 rounded-md border flex-wrap"
        style={{ borderColor: `${GREEN}55`, background: 'rgba(0,15,5,0.7)' }}
      >
        <span className="text-[11px] font-mono tracking-widest" style={{ color: GREEN, textShadow: `0 0 6px ${GREEN}` }}>
          NIGHTBUZZ OS v1.0.0
        </span>
        {overview && (
          <div className="flex items-center gap-3 text-[10px] font-mono tracking-widest">
            <span style={{ color: GREEN }}>DAU <span className="text-white font-bold">{overview.dau}</span></span>
            <span style={{ color: CYAN }}>WAU <span className="text-white font-bold">{overview.wau}</span></span>
            <span style={{ color: GOLD }}>MRR <span className="text-white font-bold">${overview.mrr.toLocaleString()}</span></span>
            <span style={{ color: RED }}>CHURN <span className="text-white font-bold">{overview.churn_pct}%</span></span>
            <span style={{ color: GREEN }}>BUZZ <span className="text-white font-bold">{overview.buzz_score}</span> · {overview.buzz_status}</span>
          </div>
        )}
        <span className="text-[11px] font-mono tracking-widest flex items-center gap-2" style={{ color: GREEN }}>
          <button
            onClick={() => setAlertsOpen(true)}
            data-testid="alerts-bell-btn"
            className="relative p-1 rounded hover:bg-emerald-900/30 transition"
            title="Alerts"
          >
            <Bell className="w-4 h-4" style={{ color: GREEN }} />
            {(overview && systemHealth) && (() => {
              const services = systemHealth?.services || {};
              const bad = Object.values(services).filter(v => v !== 'ONLINE').length;
              if (bad > 0) {
                return <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full text-[8px] flex items-center justify-center font-bold" style={{ background: RED, color: 'white', boxShadow: `0 0 6px ${RED}` }}>{bad}</span>;
              }
              return null;
            })()}
          </button>
          {(() => {
            const services = systemHealth?.services || {};
            const anyOffline = Object.values(services).some(v => v === 'OFFLINE');
            const anyDegraded = Object.values(services).some(v => v === 'DEGRADED');
            const state = anyOffline ? 'DEGRADED' : anyDegraded ? 'DEGRADED' : 'ONLINE';
            const color = state === 'ONLINE' ? GREEN : state === 'DEGRADED' ? GOLD : RED;
            return (<>
              SYSTEM STATUS:
              <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
              <span style={{ color }}>{state}</span>
            </>);
          })()}
        </span>
      </div>

      {/* Three-column OS layout */}
      <div className={`grid grid-cols-1 ${singleView ? '' : 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)]'} gap-4`}>
        {/* =============== LEFT: PATRONS =============== */}
        {showPatrons && (
        <Card ref={patronsRef} className="bg-zinc-900/70 border border-red-500/40 p-4 relative">
          <ColumnControls view="patrons" targetRef={patronsRef} />

          <div className="mb-3">
            <p
              data-testid="col-patrons-title"
              className="font-black tracking-widest text-lg flex items-center gap-2"
              style={{ fontFamily: 'Orbitron, sans-serif', color: RED, textShadow: `0 0 8px ${RED}99` }}
            >
              <Users className="w-5 h-5" /> PATRONS
            </p>
            <p className="text-[10px] font-mono tracking-widest text-red-300/80 mt-0.5">
              DISCOVER · CONNECT · EXPERIENCE
            </p>
          </div>

          {/* Pill + menu items side by side */}
          <div className="flex items-stretch gap-3">
            <VerticalPill
              label="PATRON ANALYTICS"
              color={RED}
              glow="rgba(255,59,48,0.55)"
              testid="patron-analytics-pill"
              onClick={() => go('/home')}
            />
            <div className="flex-1 space-y-2">
              {PATRON_MENU.map((m) => (
                <MenuRow
                  key={m.label}
                  Icon={m.Icon}
                  label={m.label}
                  accent="emerald"
                  onClick={() => go(m.path)}
                  testid={`patron-menu-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                />
              ))}
            </div>
          </div>

          {/* Tutorial video */}
          <div className="mt-4 rounded-lg border p-3" style={{ borderColor: `${GOLD}55`, background: 'rgba(0,0,0,0.5)' }}>
            <p className="text-[10px] font-mono tracking-widest mb-2" style={{ color: GOLD, textShadow: `0 0 6px ${GOLD}66` }}>
              ◆ TUTORIAL VIDEO
            </p>
            <div className="rounded overflow-hidden" style={{ border: `1px solid ${GOLD}` }}>
              <video controls preload="metadata" className="w-full block bg-black" data-testid="patron-tutorial-video">
                <source src={TUTORIAL_VIDEO} type="video/mp4" />
              </video>
            </div>
            <p className="text-[10px] font-mono mt-2" style={{ color: GOLD }}>
              WHAT IF I TOLD YOU… <span className="text-white/80">let me show you how it all works…</span>
            </p>
            <ul className="text-[11px] font-mono text-emerald-200/80 mt-2 space-y-0.5 list-disc list-inside">
              <li>Getting Started</li>
              <li>For Patrons</li>
              <li>For Bar Owners</li>
              <li>Features Overview</li>
              <li>Tips &amp; Best Practices</li>
            </ul>
            <button
              onClick={() => go('/nightbuzz-tutorial.mp4')}
              data-testid="watch-full-tutorial-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded font-black tracking-widest text-[11px] text-black"
              style={{ background: GOLD, boxShadow: `0 0 16px ${GOLD}88` }}
            >
              <PlayCircle className="w-4 h-4" /> WATCH FULL TUTORIAL
            </button>
          </div>
        </Card>
        )}

        {/* =============== CENTER: MEDULLA =============== */}
        {showMedulla && (
        <Card ref={medullaRef} className="bg-zinc-900/70 border border-yellow-500/40 p-4 relative">
          <ColumnControls view="medulla" targetRef={medullaRef} />

          <div className="text-center mb-3">
            <h2
              className="font-black tracking-tight"
              style={{ fontFamily: 'Orbitron, sans-serif', color: GREEN, fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', textShadow: `0 0 18px ${GREEN}aa` }}
            >
              NIGHTBUZZ
            </h2>
            <p className="text-xs font-mono tracking-widest" style={{ color: GREEN }}>
              THE OPERATING SYSTEM FOR NIGHTLIFE
            </p>
          </div>

          {/* Stats around Medulla — flanked by drink glasses */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <HeroStat label="USERS ONLINE"    value={usersOnline}   delta="↑ 15.2%" color={GREEN} glass="🍺"  side="left" />
            <HeroStat label="OWNERS ONLINE"   value={ownersOnline}  delta="↑ 8.7%"  color={CYAN}  glass="🍸"  side="right" />
            <HeroStat label="CHECK-INS TODAY" value={checkinsToday} delta="↑ 22.3%" color={GOLD}  glass="🍷"  side="left" />
            <div className="flex items-center gap-2">
              <div
                className="rounded-lg px-3 py-2 flex-1"
                style={{ background: 'linear-gradient(180deg, rgba(0,20,5,0.9), rgba(0,0,0,0.85))', border: `1px solid ${GREEN}88`, boxShadow: `0 0 12px ${GREEN}44 inset` }}
              >
                <p className="text-[9px] tracking-widest font-mono" style={{ color: GREEN }}>LIVE CROWD RATIO</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <div>
                    <p className="text-lg font-black leading-none" style={{ color: RED, fontFamily: 'Orbitron, sans-serif' }}>{girlsPct}%</p>
                    <p className="text-[9px] font-mono" style={{ color: RED }}>GIRLS</p>
                  </div>
                  <div>
                    <p className="text-lg font-black leading-none" style={{ color: CYAN, fontFamily: 'Orbitron, sans-serif' }}>{guysPct}%</p>
                    <p className="text-[9px] font-mono" style={{ color: CYAN }}>GUYS</p>
                  </div>
                </div>
              </div>
              <span className="text-3xl sm:text-4xl leading-none select-none" style={{ filter: `drop-shadow(0 0 6px ${RED}88)` }}>🍹</span>
            </div>
          </div>

          {/* Colonel Medulla */}
          <div
            className="rounded-xl overflow-hidden mx-auto relative"
            style={{ border: `2px solid ${GOLD}55`, boxShadow: `0 0 30px ${GOLD}33`, maxWidth: 360 }}
          >
            <img src={HERO_IMG} alt="Colonel Medulla" className="w-full block" data-testid="admin-medulla-img" />
          </div>
          <div className="text-center mt-2">
            <p className="text-sm font-black tracking-widest" style={{ fontFamily: 'Orbitron, sans-serif', color: GOLD, textShadow: `0 0 8px ${GOLD}99` }}>
              COLONEL MEDULLA
            </p>
            <p className="text-[10px] font-mono tracking-widest text-yellow-200/70">COMMANDER OF INTELLIGENCE</p>
          </div>

          {/* Top Venues + Top Events */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="rounded-lg border p-2" style={{ borderColor: `${GREEN}44`, background: 'rgba(0,0,0,0.5)' }}>
              <p className="text-[10px] font-mono tracking-widest mb-1" style={{ color: GREEN }}>◆ TOP VENUES</p>
              <ol className="text-[11px] font-mono text-emerald-100 space-y-0.5">
                {topVenues.map((v) => (
                  <li key={v.rank}>
                    <button
                      onClick={() => v.id && setSelectedVenueId(v.id)}
                      data-testid={`top-venue-row-${v.rank}`}
                      className={`w-full flex justify-between text-left ${v.id ? 'hover:bg-emerald-900/30 rounded px-1 -mx-1 cursor-pointer' : 'cursor-default'}`}
                      disabled={!v.id}
                    >
                      <span>{v.rank}. {v.name}</span>
                      {'checkins' in v && <span className="text-emerald-400/70">{v.checkins}</span>}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-lg border p-2" style={{ borderColor: `${GREEN}44`, background: 'rgba(0,0,0,0.5)' }}>
              <p className="text-[10px] font-mono tracking-widest mb-1" style={{ color: GREEN }}>◆ TOP EVENTS</p>
              <ol className="text-[11px] font-mono text-emerald-100 space-y-0.5">
                {topEvents.map((e) => (
                  <li key={e.rank} className="flex justify-between">
                    <span>{e.rank}. {e.name}</span>
                    {'rsvps' in e && <span className="text-emerald-400/70">{e.rsvps}</span>}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Peak Hours + Age Groups — the "graphs in Morpheus's hands" */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="rounded-lg border p-3" style={{ borderColor: `${GOLD}66`, background: 'radial-gradient(ellipse at center, rgba(0,40,10,0.6), rgba(0,0,0,0.85))', boxShadow: `0 0 20px ${GOLD}33` }}>
              <p className="text-[11px] font-mono tracking-widest mb-2 flex items-center gap-1" style={{ color: GOLD, textShadow: `0 0 6px ${GOLD}66` }}>
                <span className="text-lg">🤚</span> PEAK HOURS
              </p>
              <div className="flex items-end justify-between h-36 gap-1">
                {peakHours.map(({ h, v }) => (
                  <div key={h} className="flex flex-col items-center gap-1 flex-1 h-full justify-end">
                    <div className="w-full rounded-t" style={{ height: `${v}%`, background: `linear-gradient(180deg, ${GOLD}, #B8860B)`, boxShadow: `0 0 8px ${GOLD}88` }} />
                    <span className="text-[10px] font-mono" style={{ color: GOLD }}>{h}</span>
                  </div>
                ))}
              </div>
              <p className="text-[9px] font-mono text-yellow-200/70 mt-1 text-center">{isRealData ? 'LIVE · REFRESHED EVERY 60s' : 'DEMO DATA · WAITING FOR CHECK-INS'}</p>
            </div>
            <div className="rounded-lg border p-3" style={{ borderColor: `${GREEN}66`, background: 'radial-gradient(ellipse at center, rgba(0,40,10,0.6), rgba(0,0,0,0.85))', boxShadow: `0 0 20px ${GREEN}33` }}>
              <p className="text-[11px] font-mono tracking-widest mb-2 flex items-center gap-1" style={{ color: GREEN, textShadow: `0 0 6px ${GREEN}66` }}>
                <span className="text-lg">✋</span> AGE GROUPS
              </p>
              <div className="flex items-center gap-3">
                <div className="rounded-full flex-shrink-0" style={{ width: 108, height: 108, background: ageDonutBg, boxShadow: `0 0 20px rgba(255,255,255,0.15)` }}>
                  <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, #000 55%, transparent 56%)' }}>
                    <span className="text-xs font-black tracking-widest" style={{ color: GOLD, fontFamily: 'Orbitron, sans-serif' }}>MIX</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  {ageGroups.map((a) => (
                    <div key={a.g} className="flex items-center gap-1.5 text-[11px] font-mono">
                      <span className="w-3 h-3 rounded-sm" style={{ background: a.color, boxShadow: `0 0 4px ${a.color}` }} />
                      <span className="text-zinc-200">{a.g}</span>
                      <span className="text-zinc-400 ml-auto">{a.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status pill tiles — real app metrics */}
          <div className="grid grid-cols-5 gap-2 mt-3">
            {(() => {
              const m = systemHealth?.app_metrics || {};
              const tiles = [
                { emoji: '🆔', label: 'AGE VERIFIED', value: m.age_verified ?? 0 },
                { emoji: '🛡️', label: 'SAFETY CHECK', value: m.safety_active ?? 0 },
                { emoji: '⚖️', label: 'RATIO', value: `${m.ratio_female_pct ?? 0}%` },
                { emoji: '⭐', label: 'RATINGS', value: m.ratings_avg ?? 0 },
                { emoji: '✅', label: 'VENUES', value: m.verified_venues ?? 0 },
              ];
              return tiles.map((t) => (
                <div key={t.label} data-testid={`status-tile-${t.label.toLowerCase().replace(/ /g,'-')}`}
                  className="rounded-lg border p-2 text-center"
                  style={{ borderColor: `${GREEN}55`, background: 'rgba(0,0,0,0.6)' }}>
                  <div className="text-lg leading-none">{t.emoji}</div>
                  <p className="text-sm font-black text-white leading-tight mt-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{t.value}</p>
                  <p className="text-[8px] font-mono tracking-widest" style={{ color: GREEN }}>{t.label}</p>
                </div>
              ));
            })()}
          </div>

          {/* Featured Partner ribbon */}
          <div
            className="mt-3 rounded-lg border-2 px-3 py-2 flex items-center justify-between"
            style={{ borderColor: GOLD, background: 'linear-gradient(90deg, rgba(255,215,0,0.15), rgba(0,0,0,0.3), rgba(255,215,0,0.15))', boxShadow: `0 0 16px ${GOLD}55` }}
          >
            <p className="text-[10px] font-mono tracking-widest" style={{ color: GOLD }}>
              ⭐ FEATURED PARTNER
            </p>
            <p className="font-black tracking-widest text-white" style={{ fontFamily: 'Orbitron, sans-serif', textShadow: `0 0 8px ${GOLD}` }}>
              VODKA VIBE
            </p>
            <p className="text-[9px] font-mono text-yellow-200/70">TONIGHT ONLY</p>
          </div>
        </Card>
        )}

        {/* =============== RIGHT: BAR OWNERS =============== */}
        {showOwners && (
        <Card ref={ownersRef} className="bg-zinc-900/70 border border-sky-500/40 p-4 relative">
          <ColumnControls view="owners" targetRef={ownersRef} />

          <div className="mb-3 text-right">
            <p
              data-testid="col-owners-title"
              className="font-black tracking-widest text-lg flex items-center gap-2 justify-end"
              style={{ fontFamily: 'Orbitron, sans-serif', color: CYAN, textShadow: `0 0 8px ${CYAN}99` }}
            >
              <BarChart3 className="w-5 h-5" /> BAR OWNERS
            </p>
            <p className="text-[10px] font-mono tracking-widest text-sky-300/80 mt-0.5">
              GROW · MANAGE · PROFIT
            </p>
          </div>

          {/* Menu items + pill side by side */}
          <div className="flex items-stretch gap-3">
            <div className="flex-1 space-y-2">
              {OWNER_MENU.map((m) => (
                <MenuRow
                  key={m.label}
                  Icon={m.Icon}
                  label={m.label}
                  accent="sky"
                  onClick={() => go(m.path)}
                  testid={`owner-menu-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                />
              ))}
            </div>
            <VerticalPill
              label="PARTNER ANALYTICS"
              color={CYAN}
              glow="rgba(0,204,255,0.55)"
              testid="partner-analytics-pill"
              onClick={() => go('/advertiser/dashboard')}
            />
          </div>

          {/* Why NightBuZz */}
          <div className="mt-4 rounded-lg border p-3" style={{ borderColor: `${CYAN}55`, background: 'rgba(0,0,0,0.5)' }}>
            <p className="text-[10px] font-mono tracking-widest mb-2" style={{ color: CYAN, textShadow: `0 0 6px ${CYAN}66` }}>
              ◆ WHY NIGHTBUZZ?
            </p>
            <ul className="space-y-2">
              {WHY_LIST.map(({ Icon, title, body }) => (
                <li key={title} className="flex items-start gap-2">
                  <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: CYAN }} />
                  <div>
                    <p className="text-[12px] font-bold text-white leading-tight">{title}</p>
                    <p className="text-[10px] text-sky-200/70 leading-tight">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Card>
        )}
      </div>

      {/* Ecosystem strip — real integration health */}
      <div>
        <p className="text-xs font-mono tracking-widest mb-2" style={{ color: GREEN, textShadow: `0 0 6px ${GREEN}66` }}>
          ◆ CONNECTED ECOSYSTEM
        </p>
        <Card className="bg-zinc-900/70 border border-zinc-700/60 p-3">
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-3">
            {ECOSYSTEM.map(({ label, url, key }) => {
              const integ = systemHealth?.integrations || {};
              const healthKey = key || label.toLowerCase().replace(/ /g, '_');
              const isUp = integ[healthKey];
              const dot = isUp === true ? GREEN : isUp === false ? RED : '#666';
              return (
                <div
                  key={label}
                  data-testid={`ecosystem-${label.toLowerCase().replace(/ /g, '-')}`}
                  className="flex flex-col items-center gap-1 relative"
                  title={`${label}: ${isUp === true ? 'ONLINE' : isUp === false ? 'OFFLINE' : 'UNKNOWN'}`}
                >
                  <div className="h-9 w-9 rounded-lg bg-zinc-800/80 border border-zinc-700 flex items-center justify-center overflow-hidden relative">
                    <img src={url} alt={label} className="h-5 w-5" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: dot, boxShadow: `0 0 4px ${dot}` }} />
                  </div>
                  <span className="text-[8px] text-zinc-400 text-center leading-tight">{label}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
      {/* Modals — venue detail + alerts drawer */}
      <AdminVenueDetailModal
        venueId={selectedVenueId}
        adminKey={adminKey}
        open={!!selectedVenueId}
        onOpenChange={(o) => !o && setSelectedVenueId(null)}
      />
      <AdminAlertsDrawer
        open={alertsOpen}
        onOpenChange={setAlertsOpen}
        adminKey={adminKey}
      />
    </div>
  );
}
